import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { enrollBiometric, webauthnSupported, bioHeaders } from "@/lib/webauthn";
import { Button } from "@/components/ui/button";
import { Fingerprint, ShieldCheck, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Carte "Biométrie" sur la page pointage : enrôlement empreinte / Face ID de l'appareil
export const BiometricCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/biometric/status", bioHeaders()).then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const enroll = async () => {
    setBusy(true);
    try {
      await enrollBiometric(api);
      toast.success("Biométrie activée sur cet appareil ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Enrôlement impossible"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer la biométrie enregistrée pour cet appareil ?")) return;
    try { await api.delete("/biometric/credentials", bioHeaders()); toast.success("Biométrie supprimée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!status) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="biometric-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${status.enrolled_here ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}>
            <Fingerprint className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-[#1E3A8A]">Biométrie de l&apos;appareil</div>
            <div className="text-xs text-slate-500">
              {status.enrolled_here
                ? <span className="text-green-700 font-semibold flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Activée — empreinte / Face ID demandée au pointage</span>
                : status.require_biometric
                  ? "Exigée par l'administration : activez-la pour sécuriser vos pointages"
                  : "Optionnelle : ajoutez une vérification par empreinte / Face ID à vos pointages"}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {status.enrolled_here ? (
            <Button size="sm" variant="outline" onClick={remove} className="text-red-600 border-red-200 gap-1" data-testid="biometric-remove-btn">
              <Trash2 className="h-3.5 w-3.5" />Supprimer
            </Button>
          ) : (
            <Button size="sm" onClick={enroll} disabled={busy || !webauthnSupported()} className="bg-[#1E3A8A] text-white gap-1" data-testid="biometric-enroll-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Fingerprint className="h-4 w-4" />Activer la biométrie</>}
            </Button>
          )}
        </div>
      </div>
      {!webauthnSupported() && <p className="text-xs text-amber-600 mt-2">Ce navigateur ne prend pas en charge WebAuthn.</p>}
    </div>
  );
};
